// frontend/src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { 
  FaBox, FaTools, FaCar, FaExclamationTriangle, FaSpinner, FaChartLine, FaCalendarCheck 
} from 'react-icons/fa';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar 
} from 'recharts';
import helpers from '../../utils/helpers';

const StatCard = ({ title, value, icon: Icon, color, loading }) => {
  const colorClasses = {
    blue: 'border-blue-500',
    yellow: 'border-yellow-500',
    green: 'border-green-500',
    red: 'border-red-500',
  };
  const iconBgClasses = {
    blue: 'bg-blue-50 text-blue-500',
    yellow: 'bg-yellow-50 text-yellow-500',
    green: 'bg-green-50 text-green-500',
    red: 'bg-red-50 text-red-500',
  };

  return (
    <div className={`bg-white p-6 rounded-lg shadow-sm border-l-4 ${colorClasses[color]} flex items-center justify-between`}>
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase mb-1">{title}</p>
        {loading ? (
          <div className="h-8 w-16 bg-gray-200 animate-pulse rounded"></div>
        ) : (
          <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
        )}
      </div>
      <div className={`p-3 rounded-full ${iconBgClasses[color]}`}>
        <Icon size={24} />
      </div>
    </div>
  );
};

function AdminDashboard() {
  const [stats, setStats] = useState({
    pending_orders: 0,
    pending_services: 0,
    active_listings: 0,
    low_stock_items: 0,
  });
  const [analytics, setAnalytics] = useState({
    sales_trend: [],
    booking_trend: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      console.log("AdminDashboard: Starting data fetch...");
      try {
        const [statsData, analyticsData] = await Promise.all([
          adminApi.getDashboardStats(),
          adminApi.getAnalyticsData()
        ]);
        
        console.log("AdminDashboard: Stats received:", statsData);
        console.log("AdminDashboard: Analytics received:", analyticsData);

        setStats(statsData);
        setAnalytics(analyticsData);
      } catch (err) {
        console.error("AdminDashboard: Failed to fetch data:", err);
        setError(err.message || "Could not load dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="pb-10">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8">Dashboard Overview</h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* 1. STATS CARDS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          title="Pending Orders" 
          value={stats.pending_orders} 
          icon={FaBox} 
          color="blue" 
          loading={loading} 
        />
        <StatCard 
          title="Pending Services" 
          value={stats.pending_services} 
          icon={FaTools} 
          color="yellow" 
          loading={loading} 
        />
        <StatCard 
          title="Active Listings" 
          value={stats.active_listings} 
          icon={FaCar} 
          color="green" 
          loading={loading} 
        />
        <StatCard 
          title="Low Stock Alerts" 
          value={stats.low_stock_items} 
          icon={FaExclamationTriangle} 
          color="red" 
          loading={loading} 
        />
      </div>

      {/* 2. CHARTS SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* SALES CHART */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <FaChartLine className="text-blue-500" /> Sales Trend (Last 7 Days)
            </h3>
          </div>
          
          <div className="h-[300px] w-full">
            {loading ? (
              <div className="h-full w-full bg-gray-50 animate-pulse rounded flex items-center justify-center text-gray-400">Loading Chart...</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.sales_trend}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5c95e6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#5c95e6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fill: '#9ca3af', fontSize: 12}} 
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fill: '#9ca3af', fontSize: 12}} 
                    tickFormatter={(value) => `₦${value/1000}k`}
                  />
                  <Tooltip 
                    formatter={(value) => helpers.formatCurrency(value, 'NGN')}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#5c95e6" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorSales)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* BOOKINGS CHART */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <FaCalendarCheck className="text-purple-500" /> Service Bookings (Last 7 Days)
            </h3>
          </div>
          
          <div className="h-[300px] w-full">
            {loading ? (
              <div className="h-full w-full bg-gray-50 animate-pulse rounded flex items-center justify-center text-gray-400">Loading Chart...</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.booking_trend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fill: '#9ca3af', fontSize: 12}} 
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fill: '#9ca3af', fontSize: 12}} 
                    allowDecimals={false}
                  />
                  <Tooltip 
                    cursor={{fill: '#f3f4f6'}}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#8b5cf6" 
                    radius={[4, 4, 0, 0]} 
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default AdminDashboard;